import type { Session } from '@supabase/supabase-js';

/**
 * Single source of truth for portal isolation.
 *
 * A user's portal is determined by their ACCOUNT ROLE, never by which login
 * page they used or by whatever userType happens to sit in localStorage:
 *   - `role: 'school_admin'`  → admin portal   (primary owner, invited admin, secondary contact)
 *   - `role: 'teacher'`       → teacher portal  (must NEVER reach the admin portal)
 *   - `role: 'cover'`         → not an admin
 *
 * A legacy account with no role is treated as an admin ONLY when it is clearly
 * the primary owner (it has no `schoolId` pointing at a *different* account).
 * Any account whose `schoolId` points elsewhere is somebody's member, not an
 * admin — so a role-less teacher can never slip through.
 */
export function isAdminSession(session: Session | null | undefined): boolean {
  const meta = (session?.user?.user_metadata ?? {}) as Record<string, unknown>;
  const role = meta.role as string | undefined;
  if (role === 'teacher' || role === 'cover') return false;
  if (role === 'school_admin') return true;
  const schoolId = meta.schoolId as string | undefined;
  return !schoolId || schoolId === session?.user?.id;
}

/** The portal this authenticated session belongs in. */
export function resolveUserType(session: Session | null | undefined): 'school' | 'teacher' {
  return isAdminSession(session) ? 'school' : 'teacher';
}
